import React, {useState, useLayoutEffect, useRef} from "react";
import {_t} from '../../utils/translations';
import bn from "../../utils/bemnames";
import {
  IDeal,
  IDropdownItem,
  IEventCompetitor,
  oddsFormats
} from "../../utils/types";
import {
  fetchStandingsData,
  ICompetitionTournaments, IStandingRow,
  IStandingsResponse
} from "../../api/match-center";
import {fetchProviderDeals} from "../../api/general";
import LoadingSpinner from "../LoadingSpinner";
import StandingsTable from "./StandingsTable";
import LoadingBlock from "../LoadingBlock";
import FilterDropdown from "../FilterDropdown";
import Dropdown from 'rc-dropdown';
import EmptyComponent from "../EmptyComponent/EmptyComponent";
import ComponentWrapper from "../MatchCenter/ComponentWrapper";
import ImgWithDefault from "../ImgWithDefault";
import {chevronDownIconBlack} from "../../utils/icons";
import Menu, {Item as MenuItem} from "rc-menu";
import {getPromotionName} from "../../utils/standings";
import {ReactComponent as BottomArrowSVG} from "../../svg/arrowbot.svg";

interface IProps {
  componentName: string,
  languageID: number;
  isMobile: boolean
  competitionId?: number | null;
  tournaments: ICompetitionTournaments[];
  oddsFormat?: string | null;
  widgetId: number;
  outLinkFolder: string | null;
  eventID?: number;
  containerWidth: number;
}

interface IDimensions {
  width: number
}

enum StandingsView {
  all = 'all',
  home = 'home',
  away = 'away',
}

const bem = bn.create('standings');

const initialStageName = 'All Teams';

const Standings: React.FC<IProps> = (props) => {
  const {
    componentName,
    languageID,
    isMobile,
    competitionId = null,
    tournaments = [],
    oddsFormat,
    widgetId,
    outLinkFolder,
    eventID,
    containerWidth
  } = props

  const [loading, setLoading] = React.useState<Boolean>(true)
  const [requestFailed, setRequestFailed] = React.useState<Boolean>(false)

  const [tableLoading, setTableLoading] = useState<Boolean>(false)
  const [tableRequestFailed, setTableRequestFailed] = useState<Boolean>(false)

  const [standingsData, setStandingsData] = useState<IStandingsResponse | null>(null)

  const [selectedTournament, setSelectedTournament] = useState<IDropdownItem | null>(tournaments.length > 0 ? {
    value: tournaments[0].data[0].tournamentId,
    name: tournaments[0].data[0].tournamentName
  } : null);

  const [selectedStage, setSelectedStage] = useState<number | string>(-1);
  const [selectedStageName, setSelectedStageName] = React.useState<string>(_t(initialStageName));
  const [providerDeals, setProviderDeals] = useState<IDeal[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<IDimensions>({width: 0});

  const dropdownItems = React.useMemo(() => {
    const initialItemsList = [
      {value: -1, title: _t(initialStageName)}
    ]

    if (standingsData === null || standingsData.table.byTournamentStage.length < 1) {
      return initialItemsList
    }

    let itemsList: any[] = []

    standingsData.table.byTournamentStage.forEach((item: any) => {
      itemsList.push({
        value: item.id,
        title: item.name
      })
    });
    itemsList.sort((a, b) => a.title > b.title ? 1 : -1);

    return [...initialItemsList, ...itemsList];
  }, [standingsData])

  const tournamentItems = React.useMemo<IDropdownItem[]>(() => {
    return tournaments[0].data.reduce((acc: any, value: any) => {
      return [
        ...acc,
        {
          value: value.tournamentId,
          name: value.tournamentName,
        }
      ]
    }, [])
  }, [tournaments])

  const standingRows = React.useMemo<IStandingRow[]>(() => {
    if (!standingsData) {
      return [];
    }

    if (selectedStage === -1) {
      return standingsData.table.rows;
    }

    const tournamentStage = standingsData.table.byTournamentStage.find(tournament => tournament.id === selectedStage)

    if (!tournamentStage) {
      return standingsData.table.rows;
    }

    return tournamentStage.competitors
  }, [standingsData, selectedStage])

  const updatedOddsFormat = React.useMemo<string>(() => {
    if (!oddsFormat)
      return oddsFormats.decimal

    return oddsFormat
  }, [oddsFormat])

  // First load of component
  const getStandingsData = () => {
    setLoading(true)

    let payload = {
      view: StandingsView.all,
      languageId: languageID,
      competitionId: competitionId,
      tournamentId: selectedTournament?.value,
      tournamentStageId: -1, //Get all stages and filter on the browser
      widgetId: widgetId,
      eventId: eventID
    }

    if (eventID)
      payload['eventId'] = eventID;

    fetchStandingsData(payload)
    .then((response: IStandingsResponse) => setStandingsData(response))
    .catch((err) => {
      setRequestFailed(true)
      setTableRequestFailed(true)
    })
    .finally(() => {
      setLoading(false)
      setTableLoading(false)
    })


    fetchProviderDeals({widgetId: widgetId}).then((response) => {
      setProviderDeals(response);
    });
  }

  const onTournamentChanged = (item: IDropdownItem | null) => {
    if (tournaments.length === 1)
      return false;
    if (item !== null) {
      setSelectedTournament(item);
    }
    setSelectedStage(-1);
  }

  // Load lineups data
  React.useEffect(getStandingsData, [languageID, selectedTournament, competitionId, widgetId, eventID]);

  const handleDropdown = React.useCallback(({key}) => {
    // Updating selected value state
    setSelectedStage(Number(key))
    // Updating Stage Group Name State on Stage Select
    const findSelectedStageName = dropdownItems.find(item => {
      return item.value === Number(key);
    })
    if (findSelectedStageName) {
      setSelectedStageName(findSelectedStageName.title);
    }
  }, [dropdownItems])

  // Fix width on client site
  useLayoutEffect(() => {
    if (containerRef.current) {
      setDimensions({
        width: containerRef.current.offsetWidth,
      });
    }
  }, [containerRef]);

  return (
    <div ref={containerRef} style={dimensions.width > 0 ? {maxWidth: `${dimensions.width}px`} : undefined}>
      {loading ? <LoadingBlock height={400} heightMobile={411} isMobile={isMobile}/> : (
        (requestFailed || standingsData?.table?.rows?.length === 0) ? (
          <EmptyComponent componentName={componentName}/>
        ) : (
          <>
            {standingsData && standingsData?.table?.rows?.length > 0 && (
              <ComponentWrapper
                title={componentName}
                children={(
                  <div className={`${bem.b()} ${isMobile ? 'mobile' : ''}`}>
                    <div className={bem.e('content-container')}>
                      <div className={bem.e('controls')}>
                        {dropdownItems.length > 2 && (
                          <Dropdown
                            trigger={['click']}
                            overlay={
                              <Menu
                                onSelect={handleDropdown}
                              >
                                {dropdownItems.map((item: any) => {
                                  return (
                                    <MenuItem
                                      key={item.value}
                                    >
                                      <div className="dropdown-mobile-event-competitor-name">
                                        {item.title}
                                      </div>
                                    </MenuItem>
                                  )
                                })}
                              </Menu>
                            }
                            animation="slide-up"
                            overlayClassName={bem.e('groups-dropdown__items')}
                          >
                            <div className={bem.e('groups-dropdown')}>
                              <div>
                                {selectedStageName}
                              </div>
                              <BottomArrowSVG preserveAspectRatio="none" className={bem.e('groups-dropdown__divider')}/>
                              <ImgWithDefault
                                className={bem.e('arrow-down')}
                                alt={'chevron-down'}
                                src={chevronDownIconBlack}
                              />
                            </div>
                          </Dropdown>
                        )}
                        {tournamentItems.length > 1 && (
                          <FilterDropdown dropdownList={tournamentItems} selectedItem={selectedTournament}
                                          onStateChange={onTournamentChanged} withDefault={true}
                          />
                        )}
                      </div>
                      {tableLoading ? <LoadingSpinner/> : tableRequestFailed ? (
                        <></>
                      ) : selectedStage === -1 ? (
                        <div className={bem.e('standing-groups')}>
                          {standingsData.table.byTournamentStage.sort((a, b) => a.name > b.name ? 1 : -1)
                          .map((item, index) => {
                            const {name, competitors} = item

                            return (
                              <div key={index}>
                                <StandingsTable
                                  standingRows={competitors}
                                  highlightCompetitorIds={standingsData.eventCompetitors.map((eventCompetitor: IEventCompetitor) => eventCompetitor.competitorId)}
                                  oddsFormat={updatedOddsFormat}
                                  outLinkFolder={outLinkFolder}
                                  tournamentStageId={selectedStage}
                                  tournamentStageName={name}
                                  providerDeals={providerDeals}
                                  containerWidth={containerWidth}
                                  standingPositions={standingsData.table.positions}
                                  tournaments={tournaments}
                                />
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <>
                          <StandingsTable
                            standingRows={standingRows}
                            highlightCompetitorIds={standingsData.eventCompetitors.map((eventCompetitor: IEventCompetitor) => eventCompetitor.competitorId)}
                            oddsFormat={updatedOddsFormat}
                            outLinkFolder={outLinkFolder}
                            tournamentStageId={selectedStage}
                            tournamentStageName={selectedStageName}
                            providerDeals={providerDeals}
                            containerWidth={containerWidth}
                            standingPositions={standingsData.table.positions}
                            tournaments={tournaments}
                          />
                        </>
                      )}
                      {/* Legend */}
                      <div className={bem.e("legend")}>
                        <div className={bem.e('colors')}>
                          {standingsData.table.positions.map((position, index) => (
                            <div key={index} className={bem.e('colors__color-block')}>
                              <div className={bem.e('colors__color-block__color')}
                                   style={{backgroundColor: position.color}}></div>
                              <div className={bem.e('colors__color-block__title')}>
                                {getPromotionName(position.code)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            )}
          </>
        )
      )}
    </div>
  )
}

export default Standings;
